import React, {FC, useMemo} from 'react';
import SimpleBar from 'simplebar-react';
import {ProjectAttachment} from 'types/attachements';
import {ProjectAttachmentTypesEnum} from 'features/studio/projects/constants/project-attachments';
import {PhotoProvider, PhotoView} from 'react-photo-view';
import {useTranslation} from 'react-i18next';
import './project-attachment-viewer.styles.scss';

interface Props {
  attachments: (ProjectAttachment & {identifier: number})[];
}

const ProjectAttachmentViewer: FC<Props> = ({attachments}) => {
  const {t} = useTranslation();

  const images = useMemo(() => {
    const imageAttachments = attachments.filter(attachment => {
      if (
        attachment.type !== ProjectAttachmentTypesEnum.IMAGES_PHOTOS &&
        attachment.type !== ProjectAttachmentTypesEnum.HUB
      ) {
        return false;
      }

      const hasImage =
        attachment.original ||
        attachment.file?.url ||
        attachment.thumb;

      return hasImage;
    });

    return imageAttachments.sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });
  }, [attachments]);

  const getImageUrl = (attachment: ProjectAttachment): string | null => {
    if (attachment.thumb) return attachment.thumb;
    if (attachment.original) return attachment.original;
    if (attachment.file?.url) return attachment.file.url;
    return null;
  };

  const isUploaded = (attachment: ProjectAttachment): boolean => {
    return !!attachment.original;
  };

  if (images.length === 0) {
    return (
      <div className="image-slider-empty">
        <p className="text-muted text-center mb-0">
          {t('editProjectAttachmentSection.noImagesAdded')}
        </p>
      </div>
    );
  }

  return (
    <div className="image-slider-container">
      <div className="image-slider-header">
        <h6 className="mb-2">
          {t('editProjectAttachmentSection.imagePreview')} ({images.length})
        </h6>
        <p className="text-muted small mb-3">
          {t('editProjectAttachmentSection.manageImagesOrder')}
        </p>
      </div>

      <SimpleBar className="image-slider-scroll">
        <PhotoProvider pullClosable={false}>
          <div className="image-slider-grid">
            {images.map((attachment, index) => {
              const imageUrl = getImageUrl(attachment);
              const uploaded = isUploaded(attachment);

              // No Image
              if (!imageUrl) {
                return (
                  <div key={attachment.identifier} className="image-slider-item">
                    <div className="image-placeholder">
                      <div className="placeholder-content">
                        <span className="placeholder-icon">📁</span>
                        <span className="placeholder-text">
                          {t('editProjectAttachmentSection.noImage')}
                        </span>
                      </div>
                    </div>
                    <div className="image-info">
                      <span className="image-order">#{attachment.order ?? index + 1}</span>
                      {attachment.is_primary_image && (
                        <span className="primary-badge">
                          {t('editProjectAttachmentSection.primary')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div key={attachment.identifier} className="image-slider-item">
                  <PhotoView src={imageUrl}>
                    <div className="image-wrapper">
                      <img
                        src={imageUrl}
                        alt={attachment.name || `Image ${attachment.order ?? index + 1}`}
                        className="slider-image"
                        loading="lazy"
                      />
                    </div>
                  </PhotoView>

                  <div className="image-info">
                    <span className="image-order">#{attachment.order ?? index + 1}</span>

                    {attachment.is_primary_image && (
                      <span className="primary-badge">
                        {t('editProjectAttachmentSection.primary')}
                      </span>
                    )}

                    {!uploaded && (
                      <span className="upload-badge">
                        {t('editProjectAttachmentSection.pending')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </PhotoProvider>
      </SimpleBar>
    </div>
  );
};

export default ProjectAttachmentViewer;
